import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import LegacyAnalytics from "./APIDeprecated";

export const isNewAnalyticsEnabled = process.env.REACT_APP_ANALYTICS_MODE !== "legacy";

export const normalizeAnalyticsSessionId = (value, newMode = isNewAnalyticsEnabled) => {
  const id = (value || "").toString().trim();
  return newMode ? id : id.toLowerCase();
};

export const analyticsResponseToRow = (event) => ({
  timestamp: event.timestamp || "",
  score: event.score ?? null,
  answer: event.transcript ?? "",
  transcript: event.transcript ?? "",
  summary: event.summary ?? "",
  feedback: event.student_evaluation ?? "",
  topics: event.topics ?? [],
  environmentId: event.environmentId ?? null,
  sessionCode: event.sessionId ?? null,
  experienceId: event.experienceId ?? null,
  insightId: event.insightId ?? null,
});

const validatedOrigin = (baseURL) => {
  let url;
  try { url = new URL(baseURL); } catch { throw new Error("New analytics requires an explicit REACT_APP_ANALYTICS_API_URL origin."); }
  if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash || url.pathname !== "/") {
    throw new Error("New analytics requires an HTTPS API origin without credentials, path, or query.");
  }
  if (url.hostname === "s1chptbuta.execute-api.us-west-2.amazonaws.com") {
    throw new Error("The legacy analytics API cannot be used in new analytics mode.");
  }
  return url.origin;
};

const safeRequestError = (error) => {
  const status = Number.isInteger(error?.response?.status) ? error.response.status : undefined;
  const messages = {
    400: "The analytics request was rejected. Check the date range and filters.",
    401: "Sign in to use analytics.",
    403: "Your account is not authorized for this organization's analytics.",
    404: "The requested analytics record was not found.",
    422: "The analytics result is too large. Narrow the date range or experience filter.",
  };
  const message = messages[status] || "The analytics request failed. Please try again.";
  const code = error?.response?.data?.error?.code;
  const safeCode = typeof code === "string" && /^[A-Z_]{1,64}$/.test(code) ? code : "ANALYTICS_REQUEST_FAILED";
  // Axios errors contain request headers and response bodies. Do not expose them
  // to existing dashboard callers that may log caught errors.
  return Object.assign(new Error(message), { response: { status, data: { error: { code: safeCode, message } } } });
};

/** Read-only bridge for the existing dashboard. Legacy mode delegates unchanged. */
export const createAnalyticsReadAPI = ({
  mode = "new", baseURL, organizationOverride = "", legacyClient = LegacyAnalytics,
  request = (url, config) => axios.get(url, config),
  getAccessToken = async () => (await fetchAuthSession()).tokens?.accessToken?.toString(),
  maxPages = 2000, maxItems = 100000,
} = {}) => ({
  async get(path, config) {
    if (mode === "legacy") return legacyClient.get(path, config);
    if (mode !== "new") throw new Error("Unsupported analytics mode. Use new or legacy.");
    const origin = validatedOrigin(baseURL);
    if (typeof path !== "string" || !/^[a-z]/.test(path)) throw new Error("Unsupported new analytics route.");
    const url = new URL(path, `${origin}/`);
    if (url.origin !== origin || url.hash) throw new Error("Unsupported new analytics route.");
    if (url.pathname === "/stats") url.pathname = "/overview";
    const isOverview = url.pathname === "/overview";
    const isDetail = /^\/sessions\/[^/]+$/.test(url.pathname);
    const isCollection = ["/questions", "/sessions"].includes(url.pathname) || /^\/questions\/[^/]+\/responses$/.test(url.pathname);
    if (!isOverview && !isDetail && !isCollection) throw new Error("Unsupported new analytics route. Legacy code lookups are not available.");
    if (organizationOverride) url.searchParams.set("organization", organizationOverride);
    if (!isOverview) url.searchParams.set("limit", "500");

    const items = [];
    const cursors = new Set();
    for (let pageNumber = 0; pageNumber < maxPages; pageNumber += 1) {
      let response;
      try {
        const token = await getAccessToken();
        if (!token) throw Object.assign(new Error("Missing access token"), { response: { status: 401 } });
        // Never inherit another baseURL, params, transform, or authorization configuration.
        response = await request(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        throw safeRequestError(error);
      }
      if (isOverview) return response;
      const page = isDetail ? response?.data?.events : response?.data;
      if (!Array.isArray(page?.items) || !(page.nextToken == null || typeof page.nextToken === "string")) {
        throw new Error("Invalid new analytics pagination response.");
      }
      items.push(...page.items);
      if (items.length > maxItems) throw new Error("Analytics result is too large. Narrow the date range or experience filter.");
      if (!page.nextToken) return { ...response, data: items };
      if (cursors.has(page.nextToken)) throw new Error("Analytics pagination did not advance. No partial result was displayed.");
      cursors.add(page.nextToken);
      url.searchParams.set("nextToken", page.nextToken);
    }
    throw new Error("Analytics pagination limit reached. Narrow the date range or experience filter.");
  },
});

export default createAnalyticsReadAPI({
  mode: isNewAnalyticsEnabled ? "new" : "legacy",
  baseURL: process.env.REACT_APP_ANALYTICS_API_URL,
  organizationOverride: process.env.REACT_APP_ANALYTICS_ORGANIZATION_ID,
});
